/**
 * snake_case → camelCase key transformer for API list/get responses.
 *
 * The API (FastAPI/SQLModel) returns JSON with snake_case attribute names
 * (`organization_id`, `is_default`, `created_at`). The portal's TypeScript
 * types use camelCase (`organizationId`, `isDefault`, `createdAt`). Without
 * normalization, fields with underscores arrive as `undefined` and cause
 * silent bugs — the worst category was `isDefault` always being `undefined`
 * so "Auto-detect (inherit) — currently: …" labels never resolved, and
 * `organizationId`/`projectId`/etc. being `undefined` made
 * `filterToScopeChain` reject every non-system row (which happened to hide
 * cross-org bleed too, until we noticed defaults weren't showing).
 *
 * Recurses through arrays AND nested objects so payloads like
 *   { secret_mount: {id, database_cluster_id, ...}, pod_results: [{pod_name, ok}] }
 * normalize to
 *   { secretMount: {id, databaseClusterId, ...}, podResults: [{podName, ok}] }
 *
 * Caveat: any nested `Record<string, unknown>` that genuinely holds
 * user-supplied keys must NOT be transformed — those subtrees are listed in
 * `OPAQUE_VALUE_KEYS` (shared with `snakeKeysDeep`) and passed through
 * verbatim in BOTH directions. `settings` (DuckDB option names like
 * `memory_limit`/`TimeZone`) is the canonical example: camelizing it broke
 * display and snake-casing corrupted mixed-case option names (GS-021).
 *
 * Use this in service layers (e.g. `infrastructureService`) for RESPONSES.
 * REQUEST bodies are handled centrally by `apiClient` via `snakeKeysDeep`
 * (the API expects snake_case and many Create/Update models are
 * `extra='forbid'`, so a camelCase body 422s) — see `snakeKeysDeep` below.
 */

import type { ApiResponse } from './apiClient';
import { applyRenamedFields } from './_legacyApiFields';

const toCamel = (key: string): string =>
  key.replace(/_([a-z0-9])/g, (_match, char: string) => char.toUpperCase());

const toSnake = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();

/**
 * Keys whose VALUES are free-form maps with user-defined keys that must NOT be
 * snake_cased (a tag named "myKey" must stay "myKey"). We still snake the key
 * itself (e.g. nodeSelector → node_selector); we just don't recurse into its
 * value. Symmetric to the camelize caveat documented above.
 */
const OPAQUE_VALUE_KEYS = new Set([
  'tags',
  'annotations',
  'labels',
  'node_selector',
  'match_labels',
  'tolerations',
  'provider_config',
  'metadata',
  // DuckDB configuration settings: keys are DuckDB option names supplied by the
  // user (e.g. memory_limit, TimeZone) and must reach the engine verbatim.
  'settings',
  // Catalog maintenance detail: counts are keyed by DuckLake operation name
  // (expire_snapshots, cleanup_old_files) and must stay verbatim.
  'last_maintenance_detail',
  // Catalog maintenance steps: keyed by step name (flush_inlined_data, metadata_db, …), verbatim.
  'maintenance_steps_effective',
]);

/**
 * camelCase → snake_case key transformer for REQUEST bodies (POST/PUT/PATCH).
 * Recurses through arrays and nested objects, but treats the values of
 * OPAQUE_VALUE_KEYS as opaque (no recursion) so user-supplied map keys survive.
 * Idempotent on already-snake_case keys (no uppercase → unchanged).
 */
export const snakeKeysDeep = (input: unknown): unknown => {
  if (Array.isArray(input)) {
    return input.map((item) => snakeKeysDeep(item));
  }
  if (input !== null && typeof input === 'object' && !(input instanceof Date)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      const sk = toSnake(k);
      out[sk] = OPAQUE_VALUE_KEYS.has(sk) ? v : snakeKeysDeep(v);
    }
    return out;
  }
  return input;
};

export const camelKeysShallow = <T = unknown>(input: unknown): T => {
  if (Array.isArray(input)) {
    return input.map((item) => camelKeysShallow(item)) as unknown as T;
  }
  if (input !== null && typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      out[toCamel(k)] = OPAQUE_VALUE_KEYS.has(k) ? v : camelKeysShallow(v);
    }
    // A field the API renamed arrives under its old name from an older API: read it as the new one.
    return applyRenamedFields(out) as T;
  }
  return input as T;
};

/**
 * ApiResponse wrapper that applies `camelKeysShallow` to the `data` payload.
 * Use in service layers around `apiClient.get/put/post` so the rest of the
 * portal sees uniformly camelCased records.
 *
 * Safe to call regardless of `source` — mocks are already camelCase so the
 * transform is a no-op for them.
 */
export const camelize = <T>(resp: ApiResponse<T>): ApiResponse<T> => ({
  ...resp,
  data: camelKeysShallow<T>(resp.data),
});
